import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, useMediaQuery, Typography } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Spinner } from 'components/UI';
import MembershipsIsEmpty from './List/MembershipsIsEmpty';
import BenefitUsage from './BenefitUsage/BenefitUsage';
import MembershipsTabs, { MembershipsTabId } from './MembershipsTabs';
import DefaultHeader from 'components/UI/V2/DefaultHeader/DefaultHeader';
import { SelectionBar } from 'components/UI/SelectionBar/SelectionBar';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import { Table } from './List/Table/Table';
import { AddMembership } from 'components/Memberships/Add/AddMembership';
import MembershipDetails from './Detail/MembershipDetails';
import { Membership } from '@spike/membership-model';
import { faPlus, faSearch } from '@fortawesome/pro-solid-svg-icons';
import {
    fetchMembershipsThunk,
    MembershipsStatus,
    changeMembershipStatusThunk,
    updateMembershipNameThunk
} from '@spike/memberships-action';
import { useApiClientWrapper } from 'hooks';
import { RootState } from 'store';
import useNonInitialEffect from '@versiondos/hooks';
import { useHasPremiumPlan } from 'hooks/premiumPlan';
import PremiumFeatureAlert from 'components/UI/PremiumFeatureAlert';
import { PaymentsPath, ServicesPath } from 'routes/Paths';
import { useHistory } from 'react-router-dom';
import { PaymentsStatus } from '@spike/payments-action';

interface Props {
    create?: string;
    serviceId?: number;
    onBack?: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            minHeight: '100%',
            display: 'flex',
            [theme.breakpoints.down('sm')]: {
                display: 'block'
            }
        },
        headWrapper: {
            backgroundColor: '#FAFAFA',
            paddingLeft: 16,
            paddingRight: 16,
            paddingBottom: 10,
            paddingTop: 20,
            [theme.breakpoints.up('md')]: {
                paddingLeft: '25px',
                paddingRight: '25px',
                paddingTop: 28
            }
        },
        wrapperContainer: {
            padding: '0',
            width: '100%',
            height: '100%'
        },
        wrapperContainerEmpty: {
            paddingTop: 30,
            paddingBottom: 30,
            paddingLeft: 24,
            paddingRight: 24
        },
        loadingContainer: {
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        },
        headerContainer: {
            paddingLeft: 0,
            paddingRight: 0,
            marginBottom: 25
        },
        addMembershipContainer: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            zIndex: 11000,
            backgroundColor: '#fff',
            width: '100%',
            height: '100%',
            padding: '20px'
        },
        title: {
            marginBottom: theme.spacing(2)
        },
        SelectionBar: {
            [theme.breakpoints.up('md')]: {
                backgroundColor: '#FFF',
                marginBottom: 10
            }
        },
        FullWidthContainer: {
            width: '100%'
        },
        tableContainer: {
            paddingBottom: '30px',
            [theme.breakpoints.down('sm')]: {
                marginTop: '10px'
            }
        },
        containerEmpty: {
            padding: '40px 20px 20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center'
        },
        imageEmpty: {
            width: '100%',
            maxWidth: '375px',
            display: 'inline-block'
        },
        titleEmpty: {
            marginTop: '20px',
            fontSize: '22px',
            fontWeight: 600,
            color: '#000'
        },
        descriptionEmpty: {
            marginTop: '10px',
            fontSize: '16px',
            color: '#000',
            maxWidth: '300px',
            marginLeft: 'auto',
            marginRight: 'auto'
        },
        titleNoMemberships: {
            maxWidth: '300px',
            marginLeft: 'auto',
            marginRight: 'auto'
        },
        searchBarContainer: {
            paddingTop: 18,
            paddingBottom: 18
        }
    })
);

export const MembershipsComponent: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const [loading, setLoading] = useState(true);
    const memberships = useSelector<RootState, Membership[]>(state => state.memberships.list);
    const membershipsStatus = useSelector<RootState, MembershipsStatus>(state => state.memberships.status);
    const listPagination = useSelector((state: RootState) => state.memberships.listPagination);
    const [showAddMembership, setShowAddMembership] = useState(false);
    const [selectedMembership, setSelectedMembership] = useState<Membership | null>(null);
    const [selectedOption, setSelectedOption] = useState('active');
    const [filteredMemberships, setFilteredMemberships] = useState<Membership[]>([]);
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [searchText, setSearchText] = useState('');
    const hasPremiumPlan = useHasPremiumPlan();
    const history = useHistory();
    const paymentsEnabled = useSelector<RootState, boolean | undefined>(
        state => state.payments.enrollmentStatus?.enabled
    );
    const paymentStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);
    const [isPaymentsLoading, setIsPaymentsLoading] = useState(true);
    const [showWarning, setShowWarning] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [editingMembershipId, setEditingMembershipId] = useState<number | null>(null);
    const [editingMembership, setEditingMembership] = useState<Membership | null>(null);
    const [activeTab, setActiveTab] = useState<MembershipsTabId>('memberships');

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const fetchMemberships = (
        pageParam = 1,
        pageSizeParam = pageSize,
        statusParam = selectedOption,
        nameParam = searchText
    ) => {
        let statusValue = '';
        if (statusParam === 'active') statusValue = 'true';
        else if (statusParam === 'inactive') statusValue = 'false';

        dispatch(fetchMembershipsThunk(apiClientWrapper, pageParam, pageSizeParam, statusValue, nameParam || ''));
    };

    useEffect(() => {
        fetchMemberships(1, pageSize, selectedOption, '');
    }, []);

    useNonInitialEffect(() => {
        switch (membershipsStatus) {
            case MembershipsStatus.GetSuccess:
                setLoading(false);
                if (selectedMembership && memberships.length > 0) {
                    const membership = memberships.find(membership => membership.id === selectedMembership.id);
                    if (membership) {
                        setSelectedMembership(membership);
                    }
                }
                break;
            case MembershipsStatus.Error:
                setLoading(false);
                break;
            case MembershipsStatus.ChangingStatus:
                setLoading(true);
                break;
            case MembershipsStatus.ChangeStatusSuccess:
                fetchMemberships();
                break;
            case MembershipsStatus.ChangeStatusError:
                setLoading(false);
                break;
            case MembershipsStatus.AddSuccess:
                setSearchText('');
                fetchMemberships(1, pageSize, selectedOption, '');
                setLoading(false);
                break;
            case MembershipsStatus.UpdateNameSuccess:
                setEditingMembershipId(null);
                setLoading(false);
                break;
            case MembershipsStatus.UpdateNameError:
                setLoading(false);
                break;
            case MembershipsStatus.UpdatingName:
                setLoading(true);
                break;
            case MembershipsStatus.Updating:
                setLoading(true);
                break;
            case MembershipsStatus.UpdateSuccess:
                setEditingMembership(null);
                setSearchText(''); // Limpiar filtro de búsqueda
                fetchMemberships(1, pageSize, selectedOption, ''); // Actualizar lista
                setLoading(false);
                break;
            case MembershipsStatus.UpdateError:
                setLoading(false);
                break;
        }
    }, [membershipsStatus]);

    useNonInitialEffect(() => {
        switch (paymentStatus) {
            case PaymentsStatus.GetEnrollmentStatusSuccess:
                setIsPaymentsLoading(false);
                break;
            case PaymentsStatus.GettingEnrollmentStatus:
                setIsPaymentsLoading(true);
                break;
            case PaymentsStatus.Error:
                setIsPaymentsLoading(false);
                break;
        }
    }, [paymentStatus]);

    useEffect(() => {
        setPage(1);
    }, [selectedOption]);

    useEffect(() => {
        if (searchText.length === 0 || searchText.length >= 3) {
            fetchMemberships(page, pageSize, selectedOption, searchText);
        }
    }, [page, pageSize, selectedOption, searchText]);

    useEffect(() => {
        if (searchText.length === 0 || searchText.length >= 3) {
            setPage(1);
        }
    }, [searchText]);

    useEffect(() => {
        setFilteredMemberships(memberships);
    }, [memberships]);

    const paginatedMemberships = filteredMemberships;
    const pagination = {
        page: listPagination?.page || page,
        pageSize: listPagination?.size || pageSize,
        totalPages: listPagination?.totalPages || 1,
        totalRecords: listPagination?.totalRecords || filteredMemberships.length
    };

    const handleChangePage = (newPage: number) => {
        setPage(newPage);
    };

    const handleChangePageSize = (newSize: number) => {
        setPageSize(newSize);
        setPage(1);
    };

    const handleToggleStatus = (membershipId: number, isActive: boolean) => {
        dispatch(changeMembershipStatusThunk(apiClientWrapper, membershipId, isActive));
    };

    const handleEditMembershipName = (membershipId: number, newName: string) => {
        setEditingMembershipId(membershipId);
        dispatch(updateMembershipNameThunk(apiClientWrapper, membershipId, newName));
    };

    const handleEditMembership = (membershipId: number) => {
        const found = memberships.find(m => m.id === membershipId);
        if (found) setEditingMembership(found);
    };

    useEffect(() => {
        if (!loading && !isPaymentsLoading) {
            if (!hasPremiumPlan || !paymentsEnabled) {
                setShowWarning(true);
            } else {
                setShowWarning(false);
            }
            setInitialLoading(false);
        }
    }, [loading, isPaymentsLoading, hasPremiumPlan, paymentsEnabled]);

    const handleCloseWarning = () => {
        setShowWarning(false);
        history.push(ServicesPath);
    };

    if (editingMembership) {
        return (
            <AddMembership
                editMembership={editingMembership}
                onClose={() => setEditingMembership(null)}
            />
        );
    }

    if (showAddMembership) {
        return <AddMembership onClose={() => setShowAddMembership(false)} />;
    }

    if (selectedMembership) {
        return (
            <MembershipDetails
                membership={selectedMembership}
                onClose={() => setSelectedMembership(null)}
                refreshMemberships={fetchMemberships}
            />
        );
    }

    const filterOptions = [
        { id: 'active', name: 'Active' },
        { id: 'inactive', name: 'Inactive' },
        { id: 'all', name: 'All' }
    ];

    const handleSelect = (id: string) => {
        setSelectedOption(id);
    };

    const renderEmptyState = () => (
        <Box className={classes.containerEmpty}>
            <img
                className={classes.imageEmpty}
                src="/images/memberships/clients-notfound.svg"
            />
            <Typography className={classes.titleEmpty}>No memberships found</Typography>
            <Typography className={classes.descriptionEmpty}>
                You currently don&apos;t have any memberships to display.
            </Typography>
        </Box>
    );

    const renderMembershipsTab = () => {
        if (loading || isPaymentsLoading) {
            return (
                <Box className={classes.loadingContainer}>
                    <Spinner />
                </Box>
            );
        }

        return (
            <>
                <Box className={classes.headWrapper}>
                    <DefaultHeader
                        title={'Memberships & Packages'}
                        buttonLabel={isMobile ? 'Add' : 'Add Membership'}
                        displayButton={true}
                        onClick={() => setShowAddMembership(true)}
                        id="memberships_add_button"
                        className={classes.headerContainer}
                        buttonIcon={faPlus}
                    />
                    <SelectionBar
                        options={filterOptions}
                        selectedId={selectedOption}
                        onSelect={handleSelect}
                        className={classes.SelectionBar}
                    />
                    <SearchBar
                        searchText={searchText}
                        onChangeSearchText={setSearchText}
                        placeholder="Search by membership name"
                        className={classes.searchBarContainer}
                    />
                </Box>
                <Box className={classes.FullWidthContainer}>
                    <Table
                        page={paginatedMemberships}
                        totalRecords={pagination.totalRecords}
                        pageNumber={pagination.page}
                        pageSize={pagination.pageSize}
                        loading={false}
                        onChangePage={handleChangePage}
                        onChangePageSize={handleChangePageSize}
                        onEditMembership={handleEditMembership}
                        onEditMembershipName={handleEditMembershipName}
                        editingMembershipId={editingMembershipId}
                        onSelectMembership={setSelectedMembership}
                        hasNextPage={pagination.page < pagination.totalPages}
                        className={classes.tableContainer}
                        onChangeStatus={handleToggleStatus}
                        showPagination={true}
                    />
                    {filteredMemberships.length === 0 && (
                        <Box className={classes.containerEmpty}>{renderEmptyState()}</Box>
                    )}
                </Box>
            </>
        );
    };

    const renderContent = () => (
        <Box className={classes.wrapperContainer}>
            <MembershipsTabs
                value={activeTab}
                onChange={setActiveTab}
            />
            {activeTab === 'benefit_usage' ? <BenefitUsage /> : renderMembershipsTab()}
        </Box>
    );

    return (
        <Box className={classes.root}>
            {initialLoading ? (
                <Box className={classes.loadingContainer}>
                    <Spinner />
                </Box>
            ) : showWarning ? (
                <PremiumFeatureAlert
                    isOpen={showWarning}
                    onCancel={handleCloseWarning}
                    text={
                        !hasPremiumPlan
                            ? 'Unlock memberships with a premium plan'
                            : 'Memberships require an active CuddlesPay account'
                    }
                />
            ) : (
                renderContent()
            )}
        </Box>
    );
};

export default MembershipsComponent;
